import { useCallback, useEffect, useRef, useState } from 'react';

import { deviceDetailsConstants } from '@nw/shared/config';
import { customEvents } from '@nw/shared/data-access';
import {
  captureSentryException,
  getDeviceDetailsObjectBasedOnKeys,
  isValidSixDigitOTP,
  sendPublicFormAnalyticsToDataLayer
} from '@nw/shared/utils';

interface OTPCredential extends Credential {
  code: string;
  [key: string]: any;
}

const useAutoReadSMS = (
  onOtpReceived: (otp: string) => void,
  formPath: string,
  enabled: boolean
) => {
  const [otp, setOtp] = useState<string>('');
  const otpReceivedRef = useRef(false);

  const startOtpListener = useCallback(() => {
    const abortController = new AbortController();
    const timeoutId = setTimeout(
      () => {
        abortController.abort();
      },
      2 * 60 * 1000
    );

    async function main() {
      try {
        if (
          'OTPCredential' in window &&
          navigator.credentials &&
          typeof navigator.credentials.get === 'function' &&
          window.isSecureContext
        ) {
          const otpOptions = { otp: { transport: ['sms'] } } as any;
          const otpCredential = (await navigator.credentials.get({
            ...otpOptions,
            signal: abortController.signal
          })) as OTPCredential;

          if (otpCredential && otpCredential.code) {
            setOtp(otpCredential.code);
            otpReceivedRef.current = true;
          }
        }
      } catch (error) {
        // Ignore expected WebOTP failures
        const maybeDomException = error as { name?: string; message?: string };
        const expectedErrorNames = [
          'AbortError',
          'NotAllowedError',
          'NotSupportedError'
        ];
        const isExpectedWebOtpError =
          !!maybeDomException?.name &&
          expectedErrorNames.includes(maybeDomException.name);

        if (!isExpectedWebOtpError) {
          captureSentryException(
            (error as Error) ??
              new Error('WebOTP retrieval failed with non-error value'),
            { context: 'useAutoReadSMS', reason: 'otp_retrieval_failure' },
            { feature: 'web_otp' }
          );
        }
      }
    }

    main();

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const cleanup = startOtpListener();
    return cleanup;
  }, [startOtpListener, enabled]);

  useEffect(() => {
    if (isValidSixDigitOTP(otp) && otpReceivedRef.current) {
      const {
        deviceType,
        deviceBrowserName,
        deviceOSName,
        deviceOSVersion,
        deviceBrowserVersion
      } = deviceDetailsConstants;

      const device_details = getDeviceDetailsObjectBasedOnKeys({
        deviceType,
        deviceBrowserName,
        deviceBrowserVersion,
        deviceOSName,
        deviceOSVersion
      });
      onOtpReceived(otp);
      sendPublicFormAnalyticsToDataLayer(customEvents.otpAutoFillSuccess, {
        form_id: formPath,
        frontend_form_path_id: formPath,
        ...device_details
      });
      otpReceivedRef.current = false;
    }
  }, [otp, onOtpReceived, formPath]);

  return otp;
};

export { useAutoReadSMS };
